<script setup lang="ts">
import { onMounted, ref, onUnmounted } from 'vue';
import { useUserManagement } from '../composables/useUserManagement';

// TODO: Add a button to add a new user
// TODO: Add a button to delete a user
// TODO: Add a button to reset a user's password
// TODO: Add a button to send an email to a user
// TODO: Add a button to send a text message to a user
// TODO: Improve small screen view
// TODO: Hide dropdown on role change?
// TODO: Pagination?

const {
  users,
  loading,
  error,
  availableRoles,
  updatingUsers,
  searchQuery,
  selectedRoles,
  updateUserRoles
} = useUserManagement();

const openDropdowns = ref<{ [key: string]: boolean }>({});
const dropdownPosition = ref({ top: 0, left: 0 });

const toggleRoleFilter = (role: string) => {
  const index = selectedRoles.value.indexOf(role);
  if (index === -1) {
    selectedRoles.value.push(role);
  } else {
    selectedRoles.value.splice(index, 1);
  }
};

const toggleDropdown = (userId: string, event: Event) => {
  // Close other dropdowns when opening a new one
  Object.keys(openDropdowns.value).forEach(key => {
    if (key !== userId) {
      openDropdowns.value[key] = false;
    }
  });
  
  const isOpening = !openDropdowns.value[userId];
  openDropdowns.value[userId] = isOpening;
  
  if (isOpening) {
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    const dropdownHeight = 200; // Approximate height of dropdown
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    
    // If there's not enough space below, position above the button
    if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
      dropdownPosition.value = {
        top: rect.top + window.scrollY - dropdownHeight - 8, // 8px offset
        left: rect.right - 224 // 224px is the width of dropdown (w-56)
      };
    } else {
      dropdownPosition.value = {
        top: rect.bottom + window.scrollY + 8, // 8px offset
        left: rect.right - 224 // 224px is the width of dropdown (w-56)
      };
    }
  }
};

const handleRoleChange = async (userId: string, role: string, currentRoles: string[], checked: boolean) => {
  const newRoles = checked 
    ? [...currentRoles, role]
    : currentRoles.filter(r => r !== role);
  
  await updateUserRoles(userId, newRoles);
};

const getUserRoles = (user: { roles?: string[] }) => {
  return user.roles || [];
};

// Close dropdowns when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.dropdown-container')) {
    Object.keys(openDropdowns.value).forEach(key => {
      openDropdowns.value[key] = false;
    });
  }
};

// Set up event listeners
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="user-management p-4 sm:p-6">
    <h1 class="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">User Management</h1>
    
    <div v-if="error" class="bg-red-100 dark:bg-red-900/50 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
      {{ error }}
    </div>

    <!-- Search and Filter Section -->
    <div class="mb-6 space-y-4">
      <!-- Search -->
      <div class="flex flex-col sm:flex-row gap-4">
        <div class="flex-1">
          <label for="search" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search Users</label>
          <input
            id="search"
            v-model="searchQuery"
            type="text"
            placeholder="Search by email or name..."
            class="w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-[#222] dark:text-white"
          />
        </div>
        
        <!-- Role Filter -->
        <div class="sm:w-72">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filter by Role</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="role in availableRoles"
              :key="role"
              @click="toggleRoleFilter(role)"
              class="px-3 py-1 rounded-full text-sm font-medium transition-colors"
              :class="[
                selectedRoles.includes(role)
                  ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              ]"
            >
              {{ role }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="relative">
      <div class="-mx-4 sm:mx-0">
        <table class="min-w-full bg-white dark:bg-[#111] border border-gray-200 dark:border-gray-800 shadow-sm rounded-lg">
          <thead class="bg-gray-50 dark:bg-[#222]">
            <tr>
              <th class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
              <th class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Roles</th>
              <th class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-800">
            <tr v-if="loading" class="animate-pulse">
              <td colspan="4" class="px-3 sm:px-6 py-4 text-gray-700 dark:text-gray-300">Loading users...</td>
            </tr>
            <tr v-else-if="users.length === 0" class="hover:bg-gray-50 dark:hover:bg-[#222]">
              <td colspan="4" class="px-3 sm:px-6 py-4 text-center text-gray-700 dark:text-gray-300">
                No users found
              </td>
            </tr>
            <tr v-else v-for="user in users" :key="user.user_id" class="hover:bg-gray-50 dark:hover:bg-[#222]">
              <td class="px-3 sm:px-6 py-4">
                <div class="flex flex-col flex-row items-left">
                  <span class="font-medium text-gray-900 dark:text-white">{{ user.name || user.email || 'N/A' }}</span>
                </div>
              </td>
              <td class="px-3 sm:px-6 py-4">
                <div class="flex flex-wrap gap-1 sm:gap-2">
                  <span 
                    v-for="role in getUserRoles(user)" 
                    :key="role"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300"
                  >
                    {{ role }}
                  </span>
                </div>
              </td>
              <td class="px-3 sm:px-6 py-4">
                <div class="relative dropdown-container">
                  <button
                    @click.stop="(e) => toggleDropdown(user.user_id, e)"
                    :disabled="updatingUsers[user.user_id]"
                    class="inline-flex justify-between items-center w-full px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#222] border border-gray-300 dark:border-gray-700 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-[#333] focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-black focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span class="whitespace-nowrap">{{ updatingUsers[user.user_id] ? 'Updating...' : 'Manage Roles' }}</span>
                    <svg class="w-5 h-5 ml-2 -mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </button>

                  <div
                    v-if="openDropdowns[user.user_id]"
                    class="fixed z-50 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-[#222] ring-1 ring-black ring-opacity-5 dark:ring-white/10 origin-top"
                    :style="{
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`
                    }"
                  >
                    <div class="py-1" role="menu">
                      <div
                        v-for="role in availableRoles"
                        :key="role"
                        class="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#333]"
                      >
                        <input
                          type="checkbox"
                          :checked="getUserRoles(user).includes(role)"
                          :disabled="updatingUsers[user.user_id]"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded dark:bg-[#333]"
                          @change="(e) => handleRoleChange(user.user_id, role, getUserRoles(user), (e.target as HTMLInputElement).checked)"
                        >
                        <span class="ml-3">{{ role }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.user-management {
  max-width: 1200px;
  margin: 0 auto;
}

@media (max-width: 640px) {
  .user-management {
    max-width: 100%;
  }
}
</style> 